package com.battleships.quiz

/**
 * Persists which question IDs a specific PERSON has already been asked.
 * Scoping this to personId (not matchId) is what makes the no-repeat
 * guarantee survive across separate matches -- a person who played
 * yesterday won't see a question again today until the whole bank
 * has been exhausted for them.
 *
 * Wire a real implementation of this to whatever you already use for
 * user data (Firestore, since this project already has firestore. Rules
 * set up, is the natural fit) -- store askedIds as an array field on
 * that person's document.
 */
interface QuestionHistoryStore {
    suspend fun loadAskedIds(personId: String): Set<String>
    suspend fun saveAskedIds(personId: String, askedIds: Set<String>)
}

/**
 * In-memory fallback so QuizSession works out of the box and in tests.
 * NOTE: this does not survive an app restart -- swap in a Firestore-backed
 * QuestionHistoryStore for real no-repeat-across-matches behavior.
 */
class InMemoryQuestionHistoryStore : QuestionHistoryStore {
    private val storage = mutableMapOf<String, Set<String>>()

    override suspend fun loadAskedIds(personId: String): Set<String> =
        storage[personId] ?: emptySet()

    override suspend fun saveAskedIds(personId: String, askedIds: Set<String>) {
        storage[personId] = askedIds
    }
}

/**
 * Call this from your game code to pull random questions and check answers.
 * Tracking is per-person, pulled from a single flat question pool (no
 * category selection/rotation).
 *
 * Usage:
 *   val quiz = QuizSession(personId = currentUserId)
 *   quiz.load()                                 // once, before the first question
 *   val q = quiz.randomQuestion()
 *   val correct = quiz.checkAnswer(q, userInput)
 *   quiz.persist()                               // after each question is served
 *
 * To wire real cross-match persistence, pass a Firestore-backed
 * QuestionHistoryStore instead of the default in-memory one:
 *   val quiz = QuizSession(personId = currentUserId, historyStore = FirestoreQuestionHistoryStore())
 */
class QuizSession(
    private val personId: String,
    private val historyStore: QuestionHistoryStore = InMemoryQuestionHistoryStore()
) {
    private val askedIds = mutableSetOf<String>()

    /** Loads this person's question history. Call once before the first randomQuestion(). */
    suspend fun load() {
        askedIds.clear()
        askedIds.addAll(historyStore.loadAskedIds(personId))
    }

    /**
     * Returns a random question this person hasn't seen yet, pulled from the
     * full question pool. Once every question has been used for this person,
     * their history resets and a fresh lap begins.
     */
    fun randomQuestion(): Question {
        val pool = QuestionBank.all
        require(pool.isNotEmpty()) { "Question bank is empty." }

        val unasked = pool.filter { it.id !in askedIds }
        val finalPool = if (unasked.isNotEmpty()) {
            unasked
        } else {
            askedIds.clear()
            pool
        }

        return finalPool.random().also { askedIds.add(it.id) }
    }

    /** Persists this person's updated history. Call after each question is served. */
    suspend fun persist() {
        historyStore.saveAskedIds(personId, askedIds)
    }

    /**
     * Checks a player's typed answer against the question's accepted answers.
     * Ignores case, leading/trailing whitespace, and internal spaces/hyphens,
     * so "Git Clone", "git-clone", and "gitclone" all match an accepted
     * answer of "gitclone".
     */
    fun checkAnswer(question: Question, userInput: String): Boolean {
        val normalized = normalize(userInput)
        return question.answers.any { normalize(it) == normalized }
    }

    /** Clears this person's history entirely, e.g. for a "reset my progress" feature. */
    suspend fun resetHistory() {
        askedIds.clear()
        historyStore.saveAskedIds(personId, askedIds)
    }

    private fun normalize(text: String): String =
        text.trim().lowercase().replace(" ", "").replace("-", "")
}
